import { Injectable, inject } from "@angular/core";
import { collection, Firestore, onSnapshot, orderBy, query } from "@angular/fire/firestore";
import { AuthService } from "@core/auth.service";
import { CatchErrorService } from "@core/catch-error.service";
import { EMPTY, Observable, of } from "rxjs";
import { catchError, map, shareReplay, switchMap } from "rxjs/operators";

/**
 * Shared Firestore helper for the Store module.
 *
 * Holds the single canonical implementation of `getAngularFireStore` and
 * `snapShotData` so that `StoreRepositoryService`, `StoreJackpotRepositoryService`
 * and `StoreJackpotTriggerDataService` no longer carry duplicate copies.
 */
@Injectable({
  providedIn: "root",
})
export class StoreFirestoreHelperService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private catchService = inject(CatchErrorService);

  private ensureAuthenticated(): Observable<boolean> {
    return this.authService.ensureValidAuth();
  }

  // Get Angular FireStore for collection
  getAngularFireStore(url: string, orderByField?: string) {
    return this.ensureAuthenticated().pipe(
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          this.authService.errorHandlerLogs("User is not authenticated, attempting to recover");
          return EMPTY;
        }

        return this.catchService.isOnline$.pipe(
          switchMap((isOnline) => {
            if (isOnline) {
              if (orderByField) {
                // Create a Firestore collection reference and apply ordering
                const collectionRef = collection(this.firestore, url);
                const orderedQuery = query(collectionRef, orderBy(orderByField, "desc"));

                // Wrap onSnapshot in a custom Observable to replicate snapshotChanges()
                return new Observable((observer) => {
                  const unsubscribe = onSnapshot(
                    orderedQuery,
                    (snapshot) => {
                      // Filter out null/undefined documents before creating actions
                      const actions = snapshot.docs
                        .filter((doc) => doc && typeof doc.data === "function")
                        .map((doc) => ({
                          payload: { doc },
                          type: "added", // Mocking the type based on use case
                        }));
                      observer.next(actions);
                    },
                    (error) => observer.error(error)
                  );
                  return () => unsubscribe();
                }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
              } else {
                // Create a Firestore collection reference without ordering
                const collectionRef = collection(this.firestore, url);

                return new Observable((observer) => {
                  const unsubscribe = onSnapshot(
                    collectionRef,
                    (snapshot) => {
                      // Filter out null/undefined documents before creating actions
                      const actions = snapshot.docs
                        .filter((doc) => doc && typeof doc.data === "function")
                        .map((doc) => ({
                          payload: { doc },
                          type: "added",
                        }));
                      observer.next(actions);
                    },
                    (error) => observer.error(error)
                  );
                  return () => unsubscribe();
                }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
              }
            } else {
              return EMPTY; // Return EMPTY if offline
            }
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          map((actions: any[]) => this.snapShotData(actions)), // Use snapShotData to process actions
          shareReplay(1)
        );
      }),
      catchError((error) => {
        this.authService.errorHandlerLogs("Error in getAngularFireStore:", error);
        return of([]);
      })
    );
  }

  snapShotData(actions: import("@angular/fire/compat/firestore").DocumentChangeAction<unknown>[]): any[] {
    // Add validation for the actions array
    if (!actions || !Array.isArray(actions)) {
      this.authService.errorHandlerLogs("Invalid actions provided to snapShotData");
      return [];
    }

    // Filter out null/undefined elements and validate structure before processing
    return actions
      .filter((a) => {
        // Ensure action exists and has the required structure
        if (!a || !a?.payload || !a?.payload?.doc) {
          this.authService.errorHandlerLogs("Invalid action structure detected, skipping");
          return false;
        }
        // Ensure the doc has a data method
        if (typeof a?.payload?.doc?.data !== "function") {
          this.authService.errorHandlerLogs("Invalid doc object without data method, skipping");
          return false;
        }
        return true;
      })
      .map((a) => {
        const data = a.payload.doc.data() as any;
        const id = a.payload.doc.id;
        return { id, ...data };
      });
  }
}
